import type { AgentToolDescriptor } from "./agentTypes";
import type { RefusalReason } from "./tools";

type Row = Record<string, unknown>;

export type MissingValue = { toolName: string; field: string };

export type FilledInValue = { field: string; value: unknown };

const REQUIRED = "zz_FieldErrorCode_Required";

export const isMissingValue = (descriptor: AgentToolDescriptor, args: Row, reason: RefusalReason): boolean =>
  reason.code === REQUIRED && reason.field !== undefined && valueAt(bodyOf(descriptor, args), reason.field) === undefined;

export const missingValues = (
  toolName: string,
  descriptor: AgentToolDescriptor,
  args: Row,
  reasons: RefusalReason[],
): MissingValue[] =>
  reasons
    .filter((reason) => isMissingValue(descriptor, args, reason))
    .map((reason) => ({ toolName, field: reason.field as string }));

export const filledInValues = (
  toolName: string,
  descriptor: AgentToolDescriptor,
  args: Row,
  missing: MissingValue[],
): FilledInValue[] => {
  const body = bodyOf(descriptor, args);
  const fields = [...new Set(missing.filter((value) => value.toolName === toolName).map(({ field }) => field))];
  return fields.flatMap((field) => {
    const value = valueAt(body, field);
    return value === undefined || value === null ? [] : [{ field, value }];
  });
};

const valueAt = (root: unknown, field: string): unknown =>
  field
    .replace(/\[(\d+)\]/g, ".$1")
    .split(".")
    .reduce<unknown>((node, key) => (Array.isArray(node) ? node[Number(key)] : isRow(node) ? node[key] : undefined), root);

const bodyOf = (descriptor: AgentToolDescriptor, args: Row): Row => {
  const name = descriptor.params.find((param) => param.in === "body")?.name;
  const body = name ? args[name] : undefined;
  return isRow(body) ? body : args;
};

const isRow = (value: unknown): value is Row => !!value && typeof value === "object" && !Array.isArray(value);
